/**
 * Map Operating Status Computation
 *
 * Determines whether a venue is currently open/closed based on its
 * operating mode, config, and timezone. Separate from POI-level schedule logic.
 */

// Re-declare types here to avoid maps→db dependency.
// Canonical definitions in packages/db/src/schema.ts.
export type OperatingMode = "always_open" | "year_round" | "seasonal";

export interface ClosureDate {
  date: string;
  recurring?: boolean;
  label?: string;
}

export interface DateOverride {
  date: string;
  open: string;
  close: string;
  label?: string;
}

export interface OperatingConfig {
  seasonStart?: string;
  seasonEnd?: string;
  operatingDays?: number[];
  defaultOpen?: string;
  defaultClose?: string;
  dayOverrides?: { day: number; open: string; close: string }[];
  closureDates?: ClosureDate[];
  dateOverrides?: DateOverride[];
}

// ── Types ──

export type OperatingState =
  | "open"
  | "closed_season"
  | "closed_today"
  | "closed_hours"
  | "closed_holiday";

export interface MapOperatingStatus {
  isOpen: boolean;
  state: OperatingState;
  closesAt?: string; // "5:00 PM"
  nextOpenTime?: string; // "9:00 AM"
  nextOpenDayOffset?: number; // 0=today, 1=tomorrow, 2+=future
  nextOpenDayOfWeek?: number; // 0=Sun..6=Sat (for offset >= 2)
  closureLabel?: string; // "Christmas Day"
  seasonStart?: string; // formatted "May 1"
  seasonEnd?: string;
}

interface VenueNow {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  dayOfWeek: number;
  mmdd: string;
  yyyymmdd: string;
  timeStr: string;
}

interface NextOpenSlot {
  offset: number; // 0=today, 1=tomorrow, 2+=future
  dayOfWeek: number; // 0=Sun..6=Sat
  openTime: string; // formatted "9:00 AM"
}

// ── Helpers ──

/** Get venue-local date parts using Intl */
function getVenueNow(timezone: string, now: Date): VenueNow {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";

  const year = get("year");
  const month = get("month");
  const day = get("day");
  let hour = get("hour");
  const minute = get("minute");

  // Intl may return "24" for midnight — normalize to "00"
  if (hour === "24") hour = "00";

  // Day of week (0=Sun)
  const dateObj = new Date(`${year}-${month}-${day}T12:00:00`);
  const dayOfWeek = dateObj.getDay();

  return {
    year,
    month, // "01"-"12"
    day, // "01"-"31"
    hour, // "00"-"23"
    minute, // "00"-"59"
    dayOfWeek,
    mmdd: `${month}-${day}`,
    yyyymmdd: `${year}-${month}-${day}`,
    timeStr: `${hour}:${minute}`, // "HH:MM"
  };
}

/** Format "HH:MM" (24h) → "9:00 AM" */
function formatTime12h(time24: string): string {
  const [h, m] = time24.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${m.toString().padStart(2, "0")} ${suffix}`;
}

/** Format "MM-DD" → "May 1" */
function formatMMDD(mmdd: string): string {
  const [m, d] = mmdd.split("-").map(Number);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[m - 1]} ${d}`;
}

/** Timezone-aware season check (same logic as schedule.ts but with tz) */
function isInSeasonTz(start: string, end: string, currentMMDD: string): boolean {
  if (start <= end) {
    return currentMMDD >= start && currentMMDD <= end;
  }
  // Wrap-around: e.g. Nov 15 – Mar 15
  return currentMMDD >= start || currentMMDD <= end;
}

/** Compute date parts for a future date offset from venueNow */
function addDays(venueNow: VenueNow, offset: number): { mmdd: string; yyyymmdd: string; dayOfWeek: number } {
  const d = new Date(`${venueNow.year}-${venueNow.month}-${venueNow.day}T12:00:00`);
  d.setDate(d.getDate() + offset);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return {
    mmdd: `${mm}-${dd}`,
    yyyymmdd: `${d.getFullYear()}-${mm}-${dd}`,
    dayOfWeek: d.getDay(),
  };
}

/** Walk forward up to 14 days to find the next day+time the venue is open */
function findNextOpenSlot(
  venueNow: VenueNow,
  config: OperatingConfig,
  mode: OperatingMode,
  checkToday: boolean,
): NextOpenSlot | undefined {
  for (let offset = checkToday ? 0 : 1; offset <= 14; offset++) {
    const candidate = addDays(venueNow, offset);

    // Season check
    if (mode === "seasonal" && config.seasonStart && config.seasonEnd) {
      if (!isInSeasonTz(config.seasonStart, config.seasonEnd, candidate.mmdd)) continue;
    }

    // Closure check
    if (config.closureDates?.some((c) =>
      c.recurring ? c.date === candidate.mmdd : c.date === candidate.yyyymmdd,
    )) continue;

    // Operating days check
    if (config.operatingDays?.length && !config.operatingDays.includes(candidate.dayOfWeek)) continue;

    // Determine open time: date override > day override > default
    let openTime = config.defaultOpen;
    const dateOv = config.dateOverrides?.find((o) => o.date === candidate.yyyymmdd);
    if (dateOv) {
      openTime = dateOv.open;
    } else {
      const dayOv = config.dayOverrides?.find((d) => d.day === candidate.dayOfWeek);
      if (dayOv) openTime = dayOv.open;
    }

    if (!openTime) continue;

    return { offset, dayOfWeek: candidate.dayOfWeek, openTime: formatTime12h(openTime) };
  }
  return undefined;
}

// ── Main ──

export function computeMapOperatingStatus(
  mode: OperatingMode,
  config: OperatingConfig | null,
  timezone: string,
  now?: Date,
): MapOperatingStatus {
  const currentTime = now ?? new Date();

  // always_open: never closed (but may have hours for menu display)
  if (mode === "always_open") {
    return { isOpen: true, state: "open" };
  }

  if (!config) {
    return { isOpen: true, state: "open" };
  }

  const v = getVenueNow(timezone, currentTime);

  // 1. Seasonal check
  if (mode === "seasonal" && config.seasonStart && config.seasonEnd) {
    if (!isInSeasonTz(config.seasonStart, config.seasonEnd, v.mmdd)) {
      return {
        isOpen: false,
        state: "closed_season",
        seasonStart: formatMMDD(config.seasonStart),
        seasonEnd: formatMMDD(config.seasonEnd),
      };
    }
  }

  // 2. Closure dates
  if (config.closureDates && config.closureDates.length > 0) {
    for (const closure of config.closureDates) {
      const match = closure.recurring
        ? closure.date === v.mmdd
        : closure.date === v.yyyymmdd;
      if (match) {
        const next = findNextOpenSlot(v, config, mode, false);
        return {
          isOpen: false,
          state: "closed_holiday",
          closureLabel: closure.label,
          nextOpenDayOffset: next?.offset,
          nextOpenDayOfWeek: next?.dayOfWeek,
          nextOpenTime: next?.openTime,
        };
      }
    }
  }

  // 3. Date overrides for today
  let todayOpen: string | undefined;
  let todayClose: string | undefined;
  let isDateOverride = false;

  if (config.dateOverrides && config.dateOverrides.length > 0) {
    const override = config.dateOverrides.find((o) => o.date === v.yyyymmdd);
    if (override) {
      todayOpen = override.open;
      todayClose = override.close;
      isDateOverride = true;
    }
  }

  // 4. Operating days check (skip if already using date override)
  if (!isDateOverride && config.operatingDays && config.operatingDays.length > 0) {
    if (!config.operatingDays.includes(v.dayOfWeek)) {
      const next = findNextOpenSlot(v, config, mode, false);
      return {
        isOpen: false,
        state: "closed_today",
        nextOpenDayOffset: next?.offset,
        nextOpenDayOfWeek: next?.dayOfWeek,
        nextOpenTime: next?.openTime,
      };
    }
  }

  // 5. Day overrides (per-weekday hours)
  if (!isDateOverride && config.dayOverrides && config.dayOverrides.length > 0) {
    const dayOverride = config.dayOverrides.find((d) => d.day === v.dayOfWeek);
    if (dayOverride) {
      todayOpen = dayOverride.open;
      todayClose = dayOverride.close;
    }
  }

  // Fall back to defaults
  if (!todayOpen) todayOpen = config.defaultOpen;
  if (!todayClose) todayClose = config.defaultClose;

  // 6. Hours check
  if (todayOpen && todayClose) {
    if (v.timeStr >= todayOpen && v.timeStr < todayClose) {
      return {
        isOpen: true,
        state: "open",
        closesAt: formatTime12h(todayClose),
      };
    }

    // Closed — determine next open
    const isBefore = v.timeStr < todayOpen;
    if (isBefore) {
      // Opens later today
      return {
        isOpen: false,
        state: "closed_hours",
        nextOpenTime: formatTime12h(todayOpen),
        nextOpenDayOffset: 0,
        nextOpenDayOfWeek: v.dayOfWeek,
      };
    }

    // After close — find next open day
    const next = findNextOpenSlot(v, config, mode, false);
    return {
      isOpen: false,
      state: "closed_hours",
      nextOpenTime: next?.openTime,
      nextOpenDayOffset: next?.offset,
      nextOpenDayOfWeek: next?.dayOfWeek,
    };
  }

  // No hours configured — open by default for non-always_open modes
  return { isOpen: true, state: "open" };
}
